/** Tail points down (default map popups) or up (sol / mu / …). */
export type DeliveryPopupTail = 'down' | 'up';

/**
 * Original mood PNG aspect (~443×369). Box height stays Figma 362.314;
 * width is derived so the plaque is exactly 1.2∶1.
 */
export const DELIVERY_POPUP_ASPECT_RATIO = 1.2;

const DELIVERY_POPUP_HEIGHT = 362.314;
const DELIVERY_POPUP_WIDTH = DELIVERY_POPUP_HEIGHT * DELIVERY_POPUP_ASPECT_RATIO;

/** Horizontal scale from Figma 180:347 (463.726) → 1.2∶1 width. */
export const DELIVERY_POPUP_DOWN_LAYOUT_SCALE_X = DELIVERY_POPUP_WIDTH / 463.726;
/** Horizontal scale from Figma 190:9422 (436.726) → 1.2∶1 width. */
export const DELIVERY_POPUP_UP_LAYOUT_SCALE_X = DELIVERY_POPUP_WIDTH / 436.726;

/** Default popup chrome in design px (1.2∶1). */
export const DELIVERY_POPUP_SIZE = {
  width: DELIVERY_POPUP_WIDTH,
  height: DELIVERY_POPUP_HEIGHT,
} as const;

/** Tail-up popup chrome in design px (same 1.2∶1 outer box). */
export const DELIVERY_POPUP_SIZE_UP = {
  width: DELIVERY_POPUP_WIDTH,
  height: DELIVERY_POPUP_HEIGHT,
} as const;

export type DeliveryPopupChromeLayout = {
  background: {
    left: number;
    top: number;
    width: number;
    height: number;
  };
  rewardAmount: {
    left: number;
    top: number;
    height: number;
    fontSize: number;
  };
  ticketIcon: {
    left: number;
    top: number;
    width: number;
    height: number;
    rotateDeg: number;
  };
  takeDeliveryButton: {
    left: number;
    top: number;
    width: number;
    height: number;
    textWidth: number;
    textHeight: number;
    borderRadius: number;
    borderWidth: number;
  };
};

export const DELIVERY_POPUP_LAYOUT: DeliveryPopupChromeLayout = {
  background: {
    left: 0,
    top: 0,
    width: DELIVERY_POPUP_SIZE.width,
    height: DELIVERY_POPUP_SIZE.height,
  },
  rewardAmount: {
    left: 341.41 * DELIVERY_POPUP_DOWN_LAYOUT_SCALE_X,
    top: 8.43,
    height: 47,
    fontSize: 40,
  },
  ticketIcon: {
    left: 387.79 * DELIVERY_POPUP_DOWN_LAYOUT_SCALE_X,
    top: 10.63,
    width: 55.19,
    height: 45.18,
    rotateDeg: -17.63,
  },
  takeDeliveryButton: {
    left: 62.41 * DELIVERY_POPUP_DOWN_LAYOUT_SCALE_X,
    top: 206.68,
    width: 360 * DELIVERY_POPUP_DOWN_LAYOUT_SCALE_X,
    height: 90,
    textWidth: 208,
    textHeight: 54,
    borderRadius: 15.3,
    borderWidth: 4.9,
  },
};

/**
 * Figma 190:9422 — reward_amount_text + ticket_icon on bottom tab.
 * Positions relative to group origin (20684.54, 12063.87); X scaled to 1.2∶1.
 */
export const DELIVERY_POPUP_LAYOUT_UP: DeliveryPopupChromeLayout = {
  background: {
    left: 0,
    top: 0,
    width: DELIVERY_POPUP_SIZE_UP.width,
    height: DELIVERY_POPUP_SIZE_UP.height,
  },
  rewardAmount: {
    left: 314.41 * DELIVERY_POPUP_UP_LAYOUT_SCALE_X,
    top: 309.26,
    height: 47,
    fontSize: 40,
  },
  ticketIcon: {
    left: 355.58 * DELIVERY_POPUP_UP_LAYOUT_SCALE_X,
    top: 311.26,
    width: 55.19,
    height: 45.18,
    rotateDeg: -17.63,
  },
  takeDeliveryButton: {
    left: 35.41 * DELIVERY_POPUP_UP_LAYOUT_SCALE_X,
    top: 187.4,
    width: 360 * DELIVERY_POPUP_UP_LAYOUT_SCALE_X,
    height: 90,
    textWidth: 208,
    textHeight: 54,
    borderRadius: 15.3,
    borderWidth: 4.9,
  },
};

export function getDeliveryPopupSize(tail: DeliveryPopupTail) {
  return tail === 'up' ? DELIVERY_POPUP_SIZE_UP : DELIVERY_POPUP_SIZE;
}

export function getDeliveryPopupLayout(
  tail: DeliveryPopupTail,
): DeliveryPopupChromeLayout {
  return tail === 'up' ? DELIVERY_POPUP_LAYOUT_UP : DELIVERY_POPUP_LAYOUT;
}

/** Scale origin at the tail so the plaque grows away from the giant. */
export function getDeliveryPopupTransformOrigin(tail: DeliveryPopupTail): string {
  return tail === 'up' ? '50% 100%' : '50% 0%';
}
